import LocalStorageService  from "@/js/Shared/Services/Storage/LocalStorageService.js";
import DomContoller         from "@/js/Webmail/DomController.js"
import { reactive }         from 'vue';

export { LayoutSettingsControllerSingleton as default };

/**
* Defines all layout settings to be used by the application
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class LayoutSettingsController {
  /**
   * Defines all settings that need to be stored in the dom
   *
   * @param {Object}
   *
   * @private
   *
   * @since 7.0.0
   */
  storage = {
    theme                  : 'light-theme',
    theme_option           : 'automatic',
    theme_orientation      : 'horizontal',
    font_size              : 'font-size-default',
    color_scheme           : 'sapo-scheme',
    menu_collapsed         : false,
    menu_folders_collapsed : false,
  }

  /**
   * Defines menu toggled setting
   *
   * @param {Boolean}
   *
   * @since 7.0.0
   */
  menu_toggled = false;

  /**
   * A bridge between css and js,
   *
   * State indicator is the value set by css on media change
   *
   * @param {Boolean}
   *
   * @since 7.0.0
   */
  state_indicator = null;

  /**
   * Indicates if the device is mobile or not
   *
   * @param {Boolean}
   *
   * @since 7.0.4
   */
  is_mobile = false;

  /**
   * Constructor
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  constructor() {
    this.setMobile();
  }

  /**
   * Applies all defined settings to the dom
   *
   * @param {string} value layout mode value
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  setDomSettings() {
    DomContoller.setBodyClass(this.storage.theme);
    DomContoller.setBodyClass(this.storage.color_scheme);
    DomContoller.setHTMLClass(this.storage.font_size)
  }

  /**
   * Removes all defined settings from dom
   *
   * @param {string} value layout mode value
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  removeDomSettings() {
    DomContoller.removeBodyClass(this.storage.theme);
    DomContoller.removeBodyClass(this.storage.color_scheme);
    DomContoller.removeHTMLClass(this.storage.font_size)
  }

  /**
   * Sets Layout theme
   *
   * @param {string} value layout mode value
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  set theme(value) {
    DomContoller.removeBodyClass(this.storage.theme);
    DomContoller.setBodyClass(value);
    this.storage.theme = value;
    this.saveStorage();
  }

  /**
  * Returns Layout theme
  *
  * @return {string}
  *
  * @public
  *
  * @since 7.0.0
  */
  get theme() {
    return this.storage.theme;
  }

  /**
   * Sets Layout theme option
   *
   * @param {string} value layout mode value
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  set theme_option(value) {
    let theme = value;

    if (value === 'automatic') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      theme            = mediaQuery.matches ? 'dark-theme' : 'light-theme';
    }

    this.storage.theme_option = value;
    this.theme = theme;
  }

  /**
    * Returns Layout theme option
    *
    * @return {string}
    *
    * @public
    *
    * @since 7.0.0
    */
  get theme_option() {
    return this.storage.theme_option;
  }

  /**
   * Sets theme orientation
   *
   * @param {string} value  layout mode value
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  set theme_orientation(value) {
    this.storage.theme_orientation = value;
    this.saveStorage();
  }

  /**
   * Returns theme mode settings
   *
   * @return {string}
   *
   * @public
   *
   * @since 7.0.0
   */
  get theme_orientation() {
    return this.storage.theme_orientation
  }

  /**
   * Sets Layout theme
   *
   * @param {string} value layout mode value
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  set color_scheme(value) {
    DomContoller.removeBodyClass(this.storage.color_scheme);
    DomContoller.setBodyClass(value);
    this.storage.color_scheme = value;
    this.saveStorage();
  }

  /**
  * Returns Color Scheme
  *
  * @return {string}
  *
  * @public
  *
  * @since 7.0.0
  */
  get color_scheme() {
    return this.storage.color_scheme;
  }

  /**
   * Changes Theme Size
   *
   * @param {string} value layout mode value
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  set font_size(value) {
    DomContoller.removeHTMLClass(this.storage.font_size);
    DomContoller.setHTMLClass(value);
    this.storage.font_size = value;
    this.saveStorage();
  }

  /**
  * Returns Layout theme
  *
  * @return {string}
  *
  * @public
  *
  * @since 7.0.0
  */
  get font_size() {
    return this.storage.font_size;
  }

  /**
   * Sets Main Menu Collapsed
   *
   * As a side effect main menu folders will also be collsapsed when menu is collapsed
   *
   * @param {boolean} value  true/false
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
   set menu_collapsed(value) {
    this.storage.menu_collapsed = value;
    this.saveStorage();
  }

  /**
   * Sets Main Menu Collapsed
   *
   * @param {boolean} value  true/false
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  get menu_collapsed() {
    return this.storage.menu_collapsed;
  }

  /**
   * Sets Main Menu Folders Collapsed
   *
   * @param {boolean} value  true/false
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
   set menu_folders_collapsed(value) {
    this.storage.menu_folders_collapsed = value;
    this.saveStorage();
  }

  /**
   * Returns Main Menu Folders Collapsed value
   *
   * @param {boolean} value  true/false
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  get menu_folders_collapsed() {
    return this.storage.menu_folders_collapsed;
  }

  /**
   * saves layout settings data in storage
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
  saveStorage() {
    LocalStorageService.setObject('s_ui', this.storage);
  }

  /**
   * Sets if the device is mobile or not
   *
   * @param {Boolean}
   *
   * @since 7.0.0
   */
  setMobile() {
      if (typeof navigator === 'undefined') {
        return false;
      }

      this.is_mobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Loads data from local storage
   *
   * @return {void}
   *
   * @public
   *
   * @since 7.0.0
   */
   loadStorage() {
    let data = LocalStorageService.getObject('s_ui');

    if (data === null) {
      return;
    }

    this.storage = Object.assign(this.storage, data);
  }
}

const LayoutSettingsControllerSingleton = reactive(new LayoutSettingsController());